import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera } from "lucide-react";
import { ArrowRight, BookOpen, Users, Calendar, Award, Sparkles, Target } from 'lucide-react';
import { heroContent, aboutContent, announcements, researchAreas, latestUpdates } from '../data/mock';
import bgHero1 from "../assets/images/aboutBG.webp";
import bgHero2 from "../assets/images/bg-hero-2.webp";
import bgHero3 from "../assets/images/bg-hero-3.webp";
import bgHero4 from "../assets/images/ourTeamBG.webp";
import bgHero5 from "../assets/images/bg-hero-5.webp";
import homeAbout from "../assets/images/home-about.webp";
import gallery1 from "../assets/images/PaperCall.png";
import gallery2 from "../assets/images/publicationBG.webp";
import gallery3 from "../assets/images/activitiesBG.webp";
import gallery4 from "../assets/images/media-internship.webp";
import gallery5 from "../assets/images/interns-meeting.webp";
import gallery6 from "../assets/images/news-media-interns-1.webp";
import gyanotsava from "../assets/images/Gyanotsava_26.jpg"
import aboutGyanotsava from "../assets/images/AboutGyanotsava_26.jpg"

const eventImages = [
  { img: gyanotsava },
  { img: aboutGyanotsava },
  { img: gallery1 },
  { img: gallery2 },
  { img: gallery3 },
  { img: gallery4 },
  { img: gallery5 },
  { img: gallery6 },
];
const duplicatedEvents = [...eventImages, ...eventImages];
const duplicatedAnnouncements = [...announcements, ...announcements];


const Home = () => {

  const observerRef = useRef(null);
  const [currentBg, setCurrentBg] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const heroImages = [bgHero1, bgHero2, bgHero3, bgHero4, bgHero5];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [eventIndex, setEventIndex] = useState(0);
  const [isEventPaused, setIsEventPaused] = useState(false);
  const [selectedEventIndex, setSelectedEventIndex] = useState(null);

  const safeIndex =
    selectedEventIndex !== null
      ? selectedEventIndex % eventImages.length
      : 0;


  const visibleEvents = 4;

  useEffect(() => {
    if (isEventPaused) return;

    const interval = setInterval(() => {
      setEventIndex((prev) =>
        (prev + 1) % eventImages.length
      );
    }, 2500);

    return () => clearInterval(interval);
  }, [isEventPaused, eventImages.length]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % heroImages.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  const visibleCards = 3;

  useEffect(() => {
    if (currentIndex >= announcements.length) {
      setTimeout(() => {
        setCurrentIndex(0);
      }, 500);
    }
  }, [currentIndex, announcements.length]);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) =>
        (prev + 1) % announcements.length
      );
    }, 2500);

    return () => clearInterval(interval);
  }, [isPaused, announcements.length]);



  return (
    <div className="min-h-screen">

      <section className="min-h-screen flex items-center justify-center pt-20 relative overflow-hidden">

        {/* Background Image Carousel */}
        <div
          key={currentBg}
          className="absolute inset-0 bg-cover bg-center transition-all duration-[3000ms] ease-out"
          style={{
            backgroundImage: `url(${heroImages[currentBg]})`,
            transform: 'scale(1.05)',
            backgroundSize: 'cover',
            animation: 'zoomOut 6s ease-out forwards'
          }}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#fff7ed]/65 to-[#ffedd5]/75" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Announcement Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-5 py-2 shadow-lg animate-fade-in-up">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground uppercase tracking-wide">
                {heroContent.announcement}
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight animate-fade-in-up font-serif" style={{ animationDelay: '0.1s' }}>
              {heroContent.title}
            </h1>

            {/* Sanskrit Motto */}
            <p className="text-2xl md:text-3xl text-primary devanagari font-semibold animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              सत्यं ज्ञानमनन्तं ब्रह्म
            </p>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              {heroContent.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <Link to="/work-with-us#intern" className="btn-saffron inline-flex items-center gap-2">
                <Users className="w-5 h-5" />
                Become an Intern
              </Link>
              <Link to="/work-with-us#volunteer" className="btn-outline-saffron inline-flex items-center gap-2">
                Volunteer With Us
              </Link>
              <Link to="/academics" className="btn-outline-saffron inline-flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Explore Research
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-primary rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* 🔔 Running Notification Bar */}
      <section>
        <div className="w-full bg-white text-foreground text-sm overflow-hidden border-b border-primary/20">
          <div className="flex items-center">

            {/* Label */}
            <div className="flex-shrink-0 px-4 py-2 font-semibold tracking-wide bg-primary text-white border-r border-primary/30">
              Latest Updates
            </div>

            {/* Marquee */}
            <div className="flex-1 bg-primary/5 overflow-hidden whitespace-nowrap pointer-events-none">
              <div className="inline-flex animate-marquee items-center px-4 pointer-events-auto">
                {latestUpdates.map((item, index) => {
                  const Content = (
                    <span className="inline-flex items-center whitespace-nowrap">
                      {item.text}
                      {item.isNew && <span className="ml-1 new-indicator">NEW</span>}
                    </span>
                  );

                  return item.type === "page" ? (
                    <Link
                      key={index}
                      to={item.link}
                      className="mx-8 hover:underline"
                    >
                      {Content}
                    </Link>
                  ) : (
                    <a
                      key={index}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mx-8 hover:underline"
                    >
                      {Content}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="scroll-reveal space-y-6">
                <div className="inline-flex items-center gap-2 text-primary font-semibold uppercase tracking-wide text-sm">
                  <Target className="w-5 h-5" />
                  About VAC-IKS
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif leading-tight">
                  Bridging Ancient Wisdom with Modern Innovation
                </h2>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {aboutContent.introduction}
                </p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-4 transition-all"
                >
                  Learn More About Us
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              <div className="scroll-reveal" style={{ transitionDelay: '0.2s' }}>
                <div className="relative">
                  <img
                    src={homeAbout}
                    alt="Ancient manuscripts"
                    className="rounded-2xl shadow-2xl w-full  object-cover hover-lift"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent rounded-2xl"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">

          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
            Events & Notifications
          </h2>

          <div
            className="relative overflow-hidden group"
            onMouseEnter={() => setIsEventPaused(true)}
            onMouseLeave={() => setIsEventPaused(false)}
          >

            {/* Gradient Fade */}
            {/* <div className="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div> */}
            <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

            {/* Slider Track */}
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateX(-${(eventIndex * 100) / visibleEvents}%)`,
              }}
            >
              {duplicatedEvents.map((item, index) => (
                <div
                  key={index}
                  className="w-1/2 sm:w-1/2 md:w-1/3 lg:w-1/4 px-3 flex-shrink-0"
                >
                  <img
                    src={item.img}
                    alt="event"
                    onClick={() => setSelectedEventIndex(index % eventImages.length)}
                    className="w-full h-56 md:h-64 object-cover rounded-lg cursor-pointer hover:scale-105 transition"
                  />
                </div>
              ))}
            </div>

            {/* Arrows */}
            <button
              onClick={() =>
                setEventIndex((prev) =>
                  prev === 0 ? eventImages.length - 1 : prev - 1
                )
              }
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 p-2 rounded-full shadow opacity-0 group-hover:opacity-100"
            >
              ←
            </button>

            <button
              onClick={() =>
                setEventIndex((prev) =>
                  (prev + 1) % eventImages.length
                )
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 p-2 rounded-full shadow opacity-0 group-hover:opacity-100"
            >
              →
            </button>

          </div>

          {/* DOTS */}
          <div className="flex justify-center mt-5 gap-2">
            {eventImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setEventIndex(index)}
                className={`h-2 rounded-full transition-all ${index === eventIndex % eventImages.length
                  ? "w-6 bg-primary"
                  : "w-2 bg-gray-300"
                  }`}
              />
            ))}
          </div>

        </div>
      </section >


      {/* Latest Announcements */}
      < section className="py-20 bg-white" >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 scroll-reveal">
              <div className="inline-flex items-center gap-2 text-primary font-semibold uppercase tracking-wide text-sm mb-4">
                <Calendar className="w-5 h-5" />
                Latest Updates
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground font-serif">
                Announcements & Activities
              </h2>
            </div>

            <div
              className="relative overflow-hidden"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Left Fade */}
              <div className="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>

              {/* Right Fade */}
              <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

              <button
                onClick={() =>
                  setCurrentIndex((prev) =>
                    prev === 0 ? announcements.length - 1 : prev - 1
                  )
                }
                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur p-2 rounded-full shadow hover:scale-110 transition"
              >
                ←
              </button>

              <button
                onClick={() =>
                  setCurrentIndex((prev) =>
                    (prev + 1) % (announcements.length)
                  )
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur p-2 rounded-full shadow hover:scale-110 transition"
              >
                →
              </button>

              <div
                className="flex transition-transform duration-700 ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * (100 / visibleCards)}%)`
                }}
              >
                {duplicatedAnnouncements.map((announcement, index) => (
                  <div
                    key={`${announcement.id}-${index}`}
                    className="w-full sm:w-1/2 md:w-1/3 px-3 flex-shrink-0"
                  >
                    <Link
                      to={announcement.link}
                      className="block bg-gradient-to-br from-white to-secondary/20 p-6 rounded-xl shadow-lg border border-primary/10 hover-lift h-full"
                    >
                      <div className="text-sm text-primary font-semibold mb-2">
                        {new Date(announcement.date).toDateString()}
                      </div>

                      <h3 className="text-lg font-bold mb-2">
                        {announcement.title}
                      </h3>

                      <p className="text-sm text-muted-foreground mb-3">
                        {announcement.excerpt}
                      </p>

                      <span className="text-primary font-semibold text-sm">
                        Read More →
                      </span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center mt-6 gap-2">
              {announcements.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex % announcements.length
                    ? "w-6 bg-primary"
                    : "w-2 bg-gray-300"
                    }`}
                />
              ))}
            </div>

            <div className="text-center mt-12 scroll-reveal">
              <Link to="/activities" className="btn-outline-saffron inline-flex items-center gap-2">
                View All Activities
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section >


      {/* Key Focus Areas */}
      < section className="py-20 bg-secondary/30" >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 scroll-reveal">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-serif">
                Our Research Focus Areas
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Exploring the depth and breadth of Indian Knowledge Systems
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {researchAreas.map((area, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-lg hover-lift card-shine scroll-reveal"
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {area.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {area.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12 scroll-reveal">
              <Link to="/academics" className="btn-saffron inline-flex items-center gap-2">
                Explore All Programs
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section >


      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">

            {/* Section Header */}
            <div className="flex items-center gap-3 mb-10 justify-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Camera className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground font-serif">
                Centre Activities & Events
              </h2>
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {[
                gallery1, gallery2, gallery3, gallery4, gallery5, gallery6
              ].map((img, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl shadow-lg"
                >
                  <img
                    src={img}
                    alt={`VAC-IKS Activity ${index + 1}`}
                    loading="lazy"
                    onClick={() => setSelectedImage(img)}
                    className="w-full h-64 object-cover cursor-pointer transition-transform duration-500 group-hover:scale-110"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-sm tracking-wide uppercase">
                      View Activity
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center mt-10">
              <Link to="/gallery" className="btn-saffron">
                View Full Gallery
              </Link>
            </div>

          </div>
        </div>
      </section>


      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-primary to-orange-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold font-serif">
              Join Us in Rediscovering India's Timeless Wisdom
            </h2>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              Be part of a transformative journey that bridges ancient knowledge with modern innovation
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/work-with-us"
                className="bg-white text-primary font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-all hover:scale-105 shadow-xl"
              >
                Work With Us
              </Link>
              <Link
                to="/contact"
                className="bg-transparent border-2 border-white text-white font-bold px-8 py-4 rounded-full hover:bg-white hover:text-primary transition-all"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {
        selectedEventIndex !== null && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setSelectedEventIndex(null)}
          >

            {/* LEFT ARROW */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEventIndex((prev) =>
                  prev === 0 ? eventImages.length - 1 : prev - 1
                );
              }}
              className="absolute left-6 text-white text-3xl bg-black/40 px-3 py-2 rounded-full hover:scale-110 transition"
            >
              ←
            </button>

            <button
              onClick={() => setSelectedEventIndex(null)}
              className="absolute top-4 left-6 text-white text-xl"
            >
              ✕
            </button>

            <div className="absolute top-4 right-6 text-white text-sm">
              {selectedEventIndex + 1} / {eventImages.length}
            </div>

            {/* IMAGE */}
            <img
              src={eventImages[safeIndex].img}
              alt="preview"
              onClick={(e) => e.stopPropagation()}
              className="w-[80%] max-h-[80%] object-contain rounded-xl shadow-2xl"
            />

            {/* RIGHT ARROW */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEventIndex((prev) =>
                  (prev + 1) % eventImages.length
                );
              }}
              className="absolute right-6 text-white text-3xl bg-black/40 px-3 py-2 rounded-full hover:scale-110 transition"
            >
              →
            </button>

          </div>
        )
      }

    </div >
  );
};



export default Home;
